/**
 * Integrytas Group - Base App (MOBILE)
 * Page: EDS.CAMPAIGN.LIST — mobile-native.
 *
 * Touch-first counterpart of src/pages/eds/EdsCampaignListPage.tsx. A DISTINCT
 * presentation (KPI tiles + dense card list + bottom-sheet detail + edit sheet)
 * over the SAME data/logic layer as the desktop page:
 *   - reads via apiDb (eds_campaign + eds_campaign_tl for the localized name,
 *     eds_email_template for the resolved template name, eds_campaign_status(+_tl)
 *     for the localized status label / is_terminal flag),
 *   - all status/override/schedule edits route through the Common Routine
 *     (routedUpdate) exactly like the desktop page — §2 single write path.
 *
 * It never re-renders the desktop grid (§16). Campaign-name translations and the
 * recipient-list assignment stay on desktop (composite-key writes) — mirroring how
 * MobileUserEditSheet keeps role assignment read-only.
 */

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Search, Inbox, Megaphone, SquarePen, X } from 'lucide-react';
import { apiDb } from '../../lib/api_client';
import { useActiveLocale } from '../../lib/use_active_locale';
import { useFormatDateTime } from '../../lib/format_date';
import { routedUpdate } from '../../lib/routed_writes';
import { MobilePageHeader, MobileStatStrip, MobileContentCard, MobileCardRow, MobileDetailRow, MobileFilterChips, MobileStatusPill } from '../components';
import { MobileRelatedChildren } from '../components/MobileRelatedChildren';
import type { MobileLeadTone, MobilePillTone } from '../components';
import type { MobilePageComponentProps } from '../mobile_page_components';
import { hasAccess } from '../../types/auth';

const PAGE_CODE = 'EDS.CAMPAIGN.LIST';

interface MobileCampaignRow {
  campaign_id: string;
  internal_code: string;
  status_code: string;
  campaign_name: string;
  template_id: string;
  template_display_name: string;
  from_name_override: string | null;
  from_email_override: string | null;
  scheduled_send_at: string | null;
  started_at: string | null;
  completed_at: string | null;
  is_terminal: boolean;
}

interface MobileStatus {
  status_code: string;
  label: string;
  sort_order: number;
  is_terminal: boolean;
}

interface TlLangRow {
  language_id: string;
  campaign_name?: string;
}

interface StatusTlRow {
  status_code: string;
  language_id: string;
  display_text: string;
}

// Status → pill / lead tone, keyed on the canonical status CODE (not the display
// label). Unknown/draft fall back to the neutral info pill and the slate lead.
function statusPillTone(code: string): MobilePillTone {
  switch (code) {
    case 'sent': return 'ok';
    case 'cancelled': return 'no';
    case 'paused': return 'wait';
    case 'scheduled':
    case 'sending': return 'info';
    default: return 'info';
  }
}

function statusLeadTone(code: string): MobileLeadTone {
  switch (code) {
    case 'sent': return 'green';
    case 'cancelled': return 'red';
    case 'paused': return 'amber';
    case 'scheduled':
    case 'sending': return 'blue';
    default: return 'slate';
  }
}

export function MobileEdsCampaignListPage({ session }: MobilePageComponentProps) {
  // §6 RBAC: csm_role_page_access decides which write affordances this user
  // gets. The server refuses the write regardless (RLS via the Common
  // Routine); this stops the page offering an action that can only error.
  const canChange = hasAccess(session, PAGE_CODE, 'change');

  const { t } = useTranslation();
  const activeLocale = useActiveLocale();
  const fmtDateTime = useFormatDateTime();
  const [campaigns, setCampaigns] = useState<MobileCampaignRow[]>([]);
  const [statuses, setStatuses] = useState<MobileStatus[]>([]);
  const [filter, setFilter] = useState('');
  const [statusFacet, setStatusFacet] = useState<string>('all');
  const [selected, setSelected] = useState<MobileCampaignRow | null>(null);
  const [editing, setEditing] = useState<MobileCampaignRow | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reload = async () => {
    setBusy(true);
    setError(null);
    try {
      // Status lookup + localized labels (§17.B2/B4 — never render the raw code).
      const { data: rawStatuses, error: sErr } = await apiDb
        .from('eds_campaign_status')
        .select('status_code, sort_order, is_terminal')
        .order('sort_order');
      if (sErr) throw sErr;

      const { data: statusTls, error: stErr } = await apiDb
        .from('eds_campaign_status_tl')
        .select('status_code, language_id, display_text');
      if (stErr) throw stErr;

      const byCode: Record<string, StatusTlRow[]> = {};
      for (const r of (statusTls ?? []) as StatusTlRow[]) (byCode[r.status_code] ??= []).push(r);
      const statusLabels: Record<string, string> = {};
      for (const [code, tls] of Object.entries(byCode)) {
        const exact = tls.find((tl) => tl.language_id === activeLocale);
        const fb = tls.find((tl) => tl.language_id === 'EN_US') ?? tls[0];
        statusLabels[code] = exact?.display_text ?? fb?.display_text ?? code;
      }

      type RawStatus = { status_code: string; sort_order: number; is_terminal: 'Y' | 'N' };
      const statusRows: MobileStatus[] = ((rawStatuses ?? []) as RawStatus[]).map((s) => ({
        status_code: s.status_code,
        label: statusLabels[s.status_code] ?? s.status_code,
        sort_order: s.sort_order,
        is_terminal: s.is_terminal === 'Y',
      }));
      setStatuses(statusRows);
      const terminalByCode: Record<string, boolean> = {};
      for (const s of statusRows) terminalByCode[s.status_code] = s.is_terminal;

      // Campaigns + localized name + resolved template name.
      const { data: rawCampaigns, error: cErr } = await apiDb
        .from('eds_campaign')
        .select(`
          campaign_id, internal_code, template_id, status_code,
          from_name_override, from_email_override,
          scheduled_send_at, started_at, completed_at,
          eds_campaign_tl!left ( language_id, campaign_name ),
          eds_email_template!left ( internal_code, from_name_default )
        `)
        .order('created_at', { ascending: false });
      if (cErr) throw cErr;

      type RawCampaign = {
        campaign_id: string;
        internal_code: string;
        template_id: string;
        status_code: string;
        from_name_override: string | null;
        from_email_override: string | null;
        scheduled_send_at: string | null;
        started_at: string | null;
        completed_at: string | null;
        eds_campaign_tl?: TlLangRow[];
        eds_email_template?: { internal_code?: string; from_name_default?: string } | null;
      };

      const rows: MobileCampaignRow[] = ((rawCampaigns ?? []) as RawCampaign[]).map((c) => {
        const tls = c.eds_campaign_tl ?? [];
        const exact = tls.find((tl) => tl.language_id === activeLocale);
        const fb = tls.find((tl) => tl.language_id === 'EN_US') ?? tls[0];
        const tmpl = c.eds_email_template;
        return {
          campaign_id: c.campaign_id,
          internal_code: c.internal_code,
          status_code: c.status_code,
          campaign_name: exact?.campaign_name ?? fb?.campaign_name ?? c.internal_code,
          template_id: c.template_id,
          template_display_name: tmpl?.from_name_default ?? tmpl?.internal_code ?? c.template_id,
          from_name_override: c.from_name_override,
          from_email_override: c.from_email_override,
          scheduled_send_at: c.scheduled_send_at,
          started_at: c.started_at,
          completed_at: c.completed_at,
          is_terminal: terminalByCode[c.status_code] ?? false,
        };
      });
      setCampaigns(rows);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  // Re-run on mount and whenever the active locale changes (name + status labels follow it).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { void reload(); }, [activeLocale]);

  const statusLabel = (code: string) => statuses.find((s) => s.status_code === code)?.label ?? code;

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return campaigns.filter((c) => {
      if (statusFacet !== 'all' && c.status_code !== statusFacet) return false;
      if (!q) return true;
      return (
        c.campaign_name.toLowerCase().includes(q) ||
        c.internal_code.toLowerCase().includes(q) ||
        c.template_display_name.toLowerCase().includes(q)
      );
    });
  }, [campaigns, filter, statusFacet]);

  const facets: { key: string; label: string }[] = [
    { key: 'all', label: t('mobile.campaigns.all', 'All') },
    ...statuses.map((s) => ({ key: s.status_code, label: s.label })),
  ];

  // KPI summary over the full (unfiltered) set.
  const kpis = useMemo(() => {
    const count = (code: string) => campaigns.filter((c) => c.status_code === code).length;
    return {
      total: campaigns.length,
      scheduled: count('scheduled'),
      sent: count('sent'),
      draft: count('draft'),
    };
  }, [campaigns]);

  const closeSheet = () => { if (!busy) setSelected(null); };

  return (
    <section className="m-screen">
      <MobilePageHeader
        title={t('mobile.campaigns.title', 'Email Campaigns')}
        description={t('mobile.campaigns.description', 'Scheduled and sent email distribution campaigns.')}
      />

      <MobileStatStrip
        stats={[
          { value: kpis.total, label: t('mobile.campaigns.kpi_total', 'Total') },
          { value: kpis.scheduled, label: t('mobile.campaigns.kpi_scheduled', 'Scheduled') },
          { value: kpis.sent, label: t('mobile.campaigns.kpi_sent', 'Sent') },
          { value: kpis.draft, label: t('mobile.campaigns.kpi_draft', 'Draft') },
        ]}
      />

      <div className="m-searchbox">
        <Search />
        <input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder={t('mobile.campaigns.search', 'Search campaigns')}
          aria-label={t('mobile.campaigns.search', 'Search campaigns')}
        />
      </div>

      <MobileFilterChips
        chips={facets.map((f) => ({ value: f.key, label: f.label }))}
        isActive={(v) => statusFacet === v}
        onSelect={(v) => setStatusFacet(v)}
      />

      {error && (
        <MobileContentCard>
          <div className="m-empty" style={{ color: 'var(--color-danger)' }}>{error}</div>
        </MobileContentCard>
      )}

      {filtered.length === 0 && !busy ? (
        <MobileContentCard><div className="m-empty"><Inbox /> {t('mobile.campaigns.none', 'No campaigns match')}</div></MobileContentCard>
      ) : (
        <MobileContentCard list>
          {filtered.map((c) => (
            <MobileCardRow
              key={c.campaign_id}
              lead={<Megaphone />}
              leadTone={statusLeadTone(c.status_code)}
              title={c.campaign_name}
              subtitle={`${c.internal_code} · ${c.template_display_name}`}
              trailing={<MobileStatusPill tone={statusPillTone(c.status_code)}>{statusLabel(c.status_code)}</MobileStatusPill>}
              chevron
              onClick={() => setSelected(c)}
            />
          ))}
        </MobileContentCard>
      )}

      {/* Detail bottom-sheet */}
      <div className={'m-scrim' + (selected ? ' m-open' : '')} onClick={closeSheet} />
      <div className={'m-sheet' + (selected ? ' m-open' : '')} aria-label={t('mobile.campaigns.detail', 'Campaign detail')}>
        <div className="m-grabber" />
        {selected && (
          <>
            <div className="m-sheet-id">
              <span className="m-av"><Megaphone size={20} /></span>
              <span>
                <div className="m-nm">{selected.campaign_name}</div>
                <div className="m-em">{selected.internal_code}</div>
              </span>
            </div>
            <MobileContentCard>
              <MobileDetailRow
                label={t('mobile.campaigns.status', 'Status')}
                value={<MobileStatusPill tone={statusPillTone(selected.status_code)}>{statusLabel(selected.status_code)}</MobileStatusPill>}
              />
              <MobileDetailRow label={t('mobile.campaigns.template', 'Template')} value={selected.template_display_name} />
              <MobileDetailRow label={t('mobile.campaigns.scheduled', 'Scheduled')} value={selected.scheduled_send_at ? fmtDateTime(selected.scheduled_send_at) : t('common.none', 'None')} />
              <MobileDetailRow label={t('mobile.campaigns.completed', 'Completed')} value={selected.completed_at ? fmtDateTime(selected.completed_at) : t('common.none', 'None')} />
              <MobileDetailRow label={t('mobile.campaigns.from_name', 'From name')} value={selected.from_name_override || t('mobile.campaigns.template_default', 'Template default')} />
              <MobileDetailRow label={t('mobile.campaigns.from_email', 'From email')} value={selected.from_email_override || t('mobile.campaigns.template_default', 'Template default')} />
            </MobileContentCard>
            {/* §17.B13 — the row's related records, stacked one group per block. */}
            <MobileContentCard>
              <MobileRelatedChildren session={session} groups={[
                  { key: 'template', title: t('mobile.campaigns.related.template', 'Template'),
                  items: selected.template_display_name ? [{ id: 'template', label: selected.template_display_name }] : [] },
                ]} />
            </MobileContentCard>
            <div className="m-actionbar">
              {canChange && (<button
                type="button"
                className="m-btn m-btn-primary"
                disabled={selected.is_terminal}
                title={selected.is_terminal ? t('mobile.campaigns.terminal_locked', 'Completed campaigns cannot be edited') : undefined}
                onClick={() => { setEditing(selected); setSelected(null); }}
              >
                <SquarePen size={16} />
                {t('mobile.campaigns.edit', 'Edit')}
              </button>)}
              <button type="button" className="m-btn m-btn-ghost" onClick={() => setSelected(null)} aria-label={t('mobile.close', 'Close')}>
                <X size={16} />
              </button>
            </div>
          </>
        )}
      </div>

      <MobileCampaignEditSheet
        session={session}
        campaign={editing}
        statuses={statuses}
        onClose={() => setEditing(null)}
        onSaved={() => { setEditing(null); void reload(); }}
      />
    </section>
  );
}

// ===========================================================================
// EDIT SHEET — scalar campaign fields via the Common Routine (routedUpdate).
// ===========================================================================

interface MobileCampaignEditSheetProps {
  session: MobilePageComponentProps['session'];
  campaign: MobileCampaignRow | null;
  statuses: MobileStatus[];
  onClose: () => void;
  onSaved: () => void;
}

// datetime-local wants 'YYYY-MM-DDTHH:mm' in local time; slice the ISO string the
// same way the desktop page does.
function toLocalInput(iso: string | null): string {
  return iso ? iso.slice(0, 16) : '';
}

function MobileCampaignEditSheet({ session, campaign, statuses, onClose, onSaved }: MobileCampaignEditSheetProps) {
  const { t } = useTranslation();
  const [statusCode, setStatusCode] = useState('');
  const [fromName, setFromName] = useState('');
  const [fromEmail, setFromEmail] = useState('');
  const [scheduledAt, setScheduledAt] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Seed the form whenever a different campaign is opened.
  useEffect(() => {
    if (!campaign) return;
    setStatusCode(campaign.status_code);
    setFromName(campaign.from_name_override ?? '');
    setFromEmail(campaign.from_email_override ?? '');
    setScheduledAt(toLocalInput(campaign.scheduled_send_at));
    setError(null);
  }, [campaign]);

  const save = async () => {
    if (!campaign) return;
    setBusy(true);
    setError(null);
    // §2: route through the Common Routine (forensic stamp + audit + hooks).
    const { error: e } = await routedUpdate(session, 'eds_campaign', 'campaign_id', campaign.campaign_id, {
      status_code: statusCode,
      from_name_override: fromName.trim() || null,
      from_email_override: fromEmail.trim() || null,
      scheduled_send_at: scheduledAt ? new Date(scheduledAt).toISOString() : null,
    });
    setBusy(false);
    if (e) { setError(e.message); return; }
    onSaved();
  };

  const open = campaign !== null;

  return (
    <>
      <div className={'m-scrim' + (open ? ' m-open' : '')} onClick={() => { if (!busy) onClose(); }} />
      <div className={'m-sheet' + (open ? ' m-open' : '')} aria-label={t('mobile.campaigns.edit', 'Edit campaign')}>
        <div className="m-grabber" />
        {campaign && (
          <>
            <div className="m-sheet-id">
              <span className="m-av"><Megaphone size={20} /></span>
              <span>
                <div className="m-nm">{t('mobile.campaigns.edit', 'Edit campaign')}</div>
                <div className="m-em">{campaign.internal_code}</div>
              </span>
            </div>

            <div className="m-fieldcard">
              <div className="m-formrow">
                <label htmlFor="m-camp-status">{t('mobile.campaigns.status', 'Status')}</label>
                <select id="m-camp-status" className="m-sel" value={statusCode} onChange={(e) => setStatusCode(e.target.value)}>
                  {statuses.map((s) => <option key={s.status_code} value={s.status_code}>{s.label}</option>)}
                </select>
              </div>
              <div className="m-formrow">
                <label htmlFor="m-camp-scheduled">{t('mobile.campaigns.scheduled', 'Scheduled send at')}</label>
                <input
                  id="m-camp-scheduled"
                  type="datetime-local"
                  className="m-inp"
                  value={scheduledAt}
                  onChange={(e) => setScheduledAt(e.target.value)}
                />
              </div>
              <div className="m-formrow">
                <label htmlFor="m-camp-fromname">{t('mobile.campaigns.from_name', 'From name override')}</label>
                <input
                  id="m-camp-fromname"
                  className="m-inp"
                  value={fromName}
                  onChange={(e) => setFromName(e.target.value)}
                  placeholder={t('mobile.campaigns.override_placeholder', 'Leave blank for template default')}
                />
              </div>
              <div className="m-formrow">
                <label htmlFor="m-camp-fromemail">{t('mobile.campaigns.from_email', 'From email override')}</label>
                <input
                  id="m-camp-fromemail"
                  type="email"
                  className="m-inp"
                  value={fromEmail}
                  onChange={(e) => setFromEmail(e.target.value)}
                  placeholder={t('mobile.campaigns.override_placeholder', 'Leave blank for template default')}
                />
              </div>
              <div className="m-formrow">
                <label>{t('mobile.campaigns.name_translations', 'Campaign name')}</label>
                <div className="m-formrow-ro">
                  {campaign.campaign_name}
                  <span className="m-formrow-hint"> · {t('mobile.campaigns.translations_desktop', 'translate on desktop')}</span>
                </div>
              </div>
            </div>

            {error && <div className="m-card"><div className="m-empty" style={{ color: 'var(--color-danger)' }}>{error}</div></div>}

            <div className="m-actionbar">
              <button type="button" className="m-btn m-btn-primary" disabled={busy} onClick={() => void save()}>
                {busy ? t('common.saving', 'Saving…') : t('common.save', 'Save changes')}
              </button>
              <button type="button" className="m-btn m-btn-ghost" disabled={busy} onClick={onClose} aria-label={t('common.cancel', 'Cancel')}>
                <X size={16} />
              </button>
            </div>
          </>
        )}
      </div>
    </>
  );
}
